import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Button, Spinner } from "@heroui/react";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuWarehouse,
    LuMapPin,
    LuPhone,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { ThemedText, PageHeader, Stepper, ErrorState } from "@/components/ui";
import { GeneralInfoStep, AddressStep, ContactStep } from "./components";

const STEPS = [
    { id: 1, title: "Informasi Umum", icon: LuWarehouse },
    { id: 2, title: "Alamat", icon: LuMapPin },
    { id: 3, title: "Kontak", icon: LuPhone },
];

const WarehouseCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [currentStep, setCurrentStep] = useState(1);

    // Contact numbers state
    const [contacts, setContacts] = useState(() =>
        isEdit ? [] : [{ contact_type_id: "", number: "" }],
    );

    // React Hook Form
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: "",
            description: "",
            address: {
                full_address: "",
                neighborhood_unit: "",
                sub_neighborhood_unit: "",
                village: "",
                district: "",
                city: "",
                province: "",
                postal_code: "",
                lat: null,
                lng: null,
                google_map_url: "",
            },
        },
    });

    // Fetch initial data for edit mode
    const fetchUrl = isEdit ? `warehouses/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    // Populate form when editing
    useEffect(() => {
        if (isEdit && initialData) {
            reset({
                name: initialData.name || "",
                description: initialData.description || "",
                address: {
                    full_address:
                        initialData.addresses?.[0]?.full_address || "",
                    neighborhood_unit:
                        initialData.addresses?.[0]?.neighborhood_unit || "",
                    sub_neighborhood_unit:
                        initialData.addresses?.[0]?.sub_neighborhood_unit || "",
                    village: initialData.addresses?.[0]?.village || "",
                    district: initialData.addresses?.[0]?.district || "",
                    city: initialData.addresses?.[0]?.city || "",
                    province: initialData.addresses?.[0]?.province || "",
                    postal_code: initialData.addresses?.[0]?.postal_code || "",
                    lat: initialData.addresses?.[0]?.lat || null,
                    lng: initialData.addresses?.[0]?.lng || null,
                    google_map_url:
                        initialData.addresses?.[0]?.google_map_url || "",
                },
            });

            // Set existing contacts
            if (
                initialData.contact_numbers &&
                initialData.contact_numbers.length > 0
            ) {
                const existingContacts = initialData.contact_numbers.map(
                    (cn) => ({
                        id: cn.id,
                        contact_type_id: cn.contact_type_id,
                        number: cn.number,
                        isExisting: true,
                    }),
                );
                setContacts(existingContacts);
            } else {
                setContacts([{ contact_type_id: "", number: "" }]);
            }
        }
    }, [isEdit, initialData, reset]);

    // Mutation
    const mutationEndpoint = isEdit
        ? `warehouses/${id}/update`
        : "warehouses/store";
    const warehouseMutation = usePostData(mutationEndpoint);

    // Validation per step
    const validateStep = async (step) => {
        switch (step) {
            case 1:
                return await trigger(["name"]);
            case 2:
                return true;
            case 3:
                return true;
            default:
                return true;
        }
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi data yang wajib diisi.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const onSubmit = (data) => {
        // Filter valid contacts (ones with both type and number)
        const validContacts = contacts.filter(
            (c) => c.contact_type_id && c.number,
        );

        // Build payload
        const payload = {
            name: data.name,
            ...(data.description && { description: data.description }),
            // Address data
            ...(data.address?.full_address && {
                address: {
                    full_address: data.address.full_address,
                    neighborhood_unit: data.address.neighborhood_unit || null,
                    sub_neighborhood_unit:
                        data.address.sub_neighborhood_unit || null,
                    village: data.address.village || null,
                    district: data.address.district || null,
                    city: data.address.city || null,
                    province: data.address.province || null,
                    postal_code: data.address.postal_code || null,
                    lat: data.address.lat || null,
                    lng: data.address.lng || null,
                    google_map_url: data.address.google_map_url || null,
                },
            }),
            // Contact numbers data - backend expects array directly
            ...(validContacts.length > 0 && {
                contact_numbers: validContacts.map((c) => ({
                    contact_type_id: c.contact_type_id,
                    number: c.number,
                })),
            }),
        };

        const method = isEdit ? "put" : "post";

        warehouseMutation.mutate(payload, {
            method,
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Gudang berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
                );
                navigate("/warehouse");
            },
            onError: (err) => {
                let message =
                    "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";
                if (err.response?.status === 422) {
                    message =
                        "Mohon periksa kembali input Anda. Pastikan semua kolom terisi dengan benar.";
                }
                showToast("error", "Gagal", message);
            },
        });
    };

    // Error state for edit mode
    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data gudang. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Gudang" : "Tambah Gudang"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Gudang", href: "/warehouse" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            {/* Stepper */}
            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={STEPS} currentStep={currentStep} />
                </CardBody>
            </Card>

            {/* Step Content */}
            <form onSubmit={(e) => e.preventDefault()} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {STEPS[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {/* Loading state for edit mode */}
                        {isEdit && isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner
                                    label="Memuat data..."
                                    color="primary"
                                />
                            </div>
                        ) : (
                            <>
                                {/* Step 1: General Info */}
                                {currentStep === 1 && (
                                    <GeneralInfoStep
                                        register={register}
                                        errors={errors}
                                    />
                                )}

                                {/* Step 2: Address */}
                                {currentStep === 2 && (
                                    <AddressStep
                                        register={register}
                                        errors={errors}
                                        watch={watch}
                                        setValue={setValue}
                                    />
                                )}

                                {/* Step 3: Contact */}
                                {currentStep === 3 && (
                                    <ContactStep
                                        contacts={contacts}
                                        setContacts={setContacts}
                                    />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Navigation Buttons */}
                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/warehouse")
                                : handlePrev
                        }
                        size="sm"
                        startContent={<LuArrowLeft size={18} />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>
                    <div className="flex gap-2">
                        {currentStep < STEPS.length ? (
                            <Button
                                type="button"
                                color="primary"
                                onPress={handleNext}
                                endContent={<LuArrowRight size={18} />}
                                size="sm"
                            >
                                Selanjutnya
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                type="button"
                                isLoading={warehouseMutation.isLoading}
                                onPress={handleSubmit(onSubmit)}
                                size="sm"
                                startContent={
                                    !warehouseMutation.isLoading && (
                                        <LuSave size={18} />
                                    )
                                }
                            >
                                {isEdit ? "Simpan Perubahan" : "Simpan Gudang"}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default WarehouseCreate;
